import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink, Row, Table } from 'reactstrap';
import BoldButton from '../../Common/BoldButton';
import { CenteredSpinner } from '../../Common/CenteredSpinner';
import ProductDto from '../../Common/dtos/ProductDto';
import RecipeDto from '../../Common/dtos/RecipeDto';
import KaAlert from '../../Common/KaAlert';
import { KaSelectOption, toKaSelectOptions } from '../../Common/KaSelect';
import OverflowTooltip from '../../Common/OverflowTooltip';
import PaginatedTable from '../../Common/Pagination/PaginatedTable';
import QueryView from '../../Common/QueryView';
import useDisplayMessage from '../../Common/useDisplayMessage';
import FilterInput from '../../FilterInput';
import DateRangePill from '../../Common/Filters/DateRangePill';
import FilterPillGroup from '../../Common/Filters/FilterPillGroup';
import SelectPill from '../../Common/Filters/SelectPill';
import { SiteFilterPill, useSiteFilter } from '../../UserAccess/SiteScope/SiteFilterPill';
import { SiteColumnCell, SiteColumnHeader } from '../../Common/SiteColumn';
import useGetProducts from '../../Requests/useGetProducts';
import useGetRecipes from '../../Requests/useGetRecipes';
import useSearchOrderTransactions from '../../../requests/useSearchOrderTransactions';
import useGetSharedSites from '../../../requests/useGetSharedSites';
import OrderTransactionDto, { TransactionStatus } from '../../Common/dtos/OrderTransactionDto';
import { AmountDisplay } from '../../Common/AmountDisplay';
import { Button } from 'react-bootstrap';
import { SingleOrderTransactionPage } from './SingleOrderTransactionPage';
import TicketImageView from './TicketImageView';
import { useSelectedBusiness } from '../../UserAccess/useSelectedBusiness';

type FilterSettings = {
  item: string;
  startDate: string;
  endDate: string;
}

type SortMode =
  { isHighlighted: boolean, field: SortField, direction: SortDirection }

enum SortField {
  number,
  loadedAt,
}

enum SortDirection {
  ascending,
  descending,
}

const defaultSort: SortMode = ({ isHighlighted: false, field: SortField.loadedAt, direction: SortDirection.descending });

const TransactionColumn = ({ children }: { children: JSX.Element }) =>
  <td className="py-2 align-middle">
    {children}
  </td>;

const OrderTransactionsListPage = () => {
  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [sortMode, setSortMode] = useState(defaultSort);

  const [filters, setFilters] = useState<FilterSettings>({
    item: '',
    startDate: '',
    endDate: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(0);
  };

  const business = useSelectedBusiness();
  const displayMessage = useDisplayMessage();

  const getProducts = useGetProducts();
  const getRecipes = useGetRecipes();
  const getSharedSites = useGetSharedSites({
    autoQuery: business.usesDataSharing,
    onError: () => displayMessage.fail('The sites shared with this business could not be loaded.'),
  });

  const siteFilter = useSiteFilter({
    sharedSites: getSharedSites.data ?? [],
    unassigned: false,
  });

  const sitesChanged = (values: string[]) => {
    siteFilter.setValues(values);
    setCurrentPage(0);
  };

  const noSitesSelected = siteFilter.siteIds.length === 0;

  const getOrderTransactions = useSearchOrderTransactions({
    page: currentPage,
    pageSize: pageSize,
    filterText: debouncedSearch,
    sortField: SortField[sortMode.field],
    sortDirection: SortDirection[sortMode.direction],
    itemId: filters.item,
    siteIds: siteFilter.siteIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
  }, { autoQuery: !noSitesSelected && !getSharedSites.isLoading });

  const { OrderTransactionNumber: initialOrderTransactionNumber, completed: initialCompleted } = useParams();

  const [orderTransactionToView, setOrderTransactionToView] = useState<OrderTransactionDto | undefined>(undefined);
  const [orderTransactionToViewTicket, setOrderTransactionToViewTicket] = useState<OrderTransactionDto | undefined>(undefined);

  useEffect(() => {
    if (initialOrderTransactionNumber) {
      numberSearchChanged(initialOrderTransactionNumber);
    }
  }, [initialOrderTransactionNumber, initialCompleted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(numberSearch);
    }, 600);

    return () => clearTimeout(handler);
  }, [numberSearch]);

  const numberSearchChanged = (number: string) => {
    setCurrentPage(0);
    setNumberSearch(number);
  };

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
  };

  const handleSortMode = (sortField: SortField) => {
    setSortMode((prev) => {
      const isSameFieldSelected = prev.isHighlighted && prev.field === sortField;

      return {
        isHighlighted: true,
        field: sortField,
        direction: isSameFieldSelected && prev.direction === SortDirection.ascending
          ? SortDirection.descending
          : SortDirection.ascending
      };
    });
  };

  const isSortColumnHighlighted = (sortField: SortField) =>
    sortMode.isHighlighted && sortField === sortMode.field;

  const getOrderTransactionItems = (OrderTransaction: OrderTransactionDto) => 
    OrderTransaction.blends.flatMap(b => b.items.map(i => i.itemName))

  const itemOptions = toKaSelectOptions<ProductDto | RecipeDto, KaSelectOption>(
    [...(getProducts.data ?? []), ...(getRecipes.data ?? [])],
    "id",
    "name",
    { sortByLabel: true }
  );

  const sortableColumnHeader = (name: string) => {
    const sortFieldMap: Record<string, SortField> = {
      Number: SortField.number,
      Date: SortField.loadedAt,
    };

    const sortField = sortFieldMap[name] ?? SortField.number;
    const isActive = isSortColumnHighlighted(sortField);
    const { direction } = sortMode;

    return <NavLink
      className="d-inline-flex align-items-center"
      active={isActive}
      onClick={() => handleSortMode(sortField)}>
      {name}
      <i className={`mx-1 ${isActive
        ? direction === SortDirection.ascending
          ? "bi-arrow-down"
          : "bi-arrow-up"
        : "bi-arrow-down-up"
        }`}
        style={{ fontSize: "0.75rem" }}
      />
    </NavLink>
  };

  const tableHeader = <thead>
    <tr className="border-bottom border-dark border-1 align-top">
      <th className="col-1">Status</th>
      <th className="col-1">
        {sortableColumnHeader("Transaction")}
      </th>
      <th className="col-2">Order</th>
      <th className="col-2">Products/Recipes</th>
      <th className="col-1">Order Quantity</th>
      <th className="col-1">Dispensed Quantity</th>
      <SiteColumnHeader singular />
      <th className="col-1">
        {sortableColumnHeader("Date")}
      </th>
      <th className="col-1" />
    </tr>
  </thead>;

  const emptyRow = <tr>
    <td colSpan={9} className="text-center text-muted py-3">
      {siteFilter.emptyMessage('No order transactions to show')}
    </td>
  </tr>;

  const refreshPage = () => {
    setOrderTransactionToView(undefined);
    setOrderTransactionToViewTicket(undefined);
    getOrderTransactions.query();
  }

  const handleVoidSuccess = () => {
    displayMessage.success(`Order transaction ${orderTransactionToView?.number} has been voided successfully.`);
    setOrderTransactionToView(undefined);
    refreshPage();
  }

  return <div>
    {orderTransactionToView ? (
      <SingleOrderTransactionPage
        orderTransaction={orderTransactionToView}
        backClicked={refreshPage}
        handleVoidSuccess={handleVoidSuccess}
      />
    ) : orderTransactionToViewTicket ? (
      <TicketImageView
        orderTransaction={orderTransactionToViewTicket}
        backClicked={refreshPage}
      />
    ) : (
      <>
        <Row className="justify-content-between mb-2">
          <h2 className="col-auto ka-blue">Order Transactions</h2>
        </Row>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <form>
          <div className="row gx-2 gx-md-3 mb-3">
            <div className="col-md-4 mb-2 mb-md-0">
              <FilterInput placeholder='Search by number or order' value={numberSearch} onFilterTextChange={numberSearchChanged} />
            </div>
          </div>
        </form>
        {getSharedSites.isLoading
          ? <div className="mt-8">
            <CenteredSpinner />
          </div>
          : <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <FilterPillGroup>
                <SiteFilterPill key="transaction-sites" filter={{ ...siteFilter, setValues: sitesChanged }} debounceMs={400} />
                <SelectPill
                  key="transaction-item"
                  id="transaction-item"
                  label="Product/Recipe"
                  icon="diagram-3"
                  value={filters.item || undefined}
                  options={itemOptions}
                  onChange={value => updateFilters({ item: value ?? '' })} />
                <DateRangePill
                  key="transaction-dates"
                  id="transaction-dates"
                  label="Date"
                  icon="calendar3"
                  range={filters.startDate && filters.endDate
                    ? { start: filters.startDate, end: filters.endDate }
                    : undefined}
                  onChange={range => updateFilters({
                    startDate: range?.start ?? '',
                    endDate: range?.end ?? '',
                  })} />
              </FilterPillGroup>
            </div>
            {noSitesSelected
              ? <Table borderless className="mt-5">
                {tableHeader}
                <tbody>
                  {emptyRow}
                </tbody>
              </Table>
              : <QueryView query={getOrderTransactions}
                renderLoading={() => <div className="mt-8">
                  <CenteredSpinner />
                </div>}
                renderData={paginatedOrderTransactions => {
                  return <PaginatedTable
                    onPageChange={handlePageChange}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalPages={paginatedOrderTransactions.totalPages}>
                    <Table borderless className="mt-5">
                      {tableHeader}
                      <tbody>
                        {paginatedOrderTransactions.items
                          .map(OrderTransaction =>
                            <tr key={OrderTransaction.id} className="border-top">
                              <TransactionColumn>
                                <span
                                  className={`${OrderTransaction.status === TransactionStatus.Completed ? 'text-primary' : 'text-danger'} fw-semibold`}>
                                  {OrderTransaction.status}
                                </span>
                              </TransactionColumn>
                              <TransactionColumn>
                                <p className="mb-0">
                                  {OrderTransaction.number}
                                </p>
                              </TransactionColumn>
                              <TransactionColumn>
                                <p className="mb-0">
                                  {OrderTransaction.orderNumber}
                                </p>
                              </TransactionColumn>
                              <TransactionColumn>
                                <OverflowTooltip className="mb-0" text={getOrderTransactionItems(OrderTransaction).join(", ")} />
                              </TransactionColumn>
                              <TransactionColumn>
                                <AmountDisplay amount={OrderTransaction.totalOrderRequested} hoverId={`requested-${OrderTransaction.id}`} />
                              </TransactionColumn>
                              <TransactionColumn>
                                <AmountDisplay amount={OrderTransaction.totalDispensed} hoverId={`dispensed-${OrderTransaction.id}`} />
                              </TransactionColumn>
                              <SiteColumnCell siteName={OrderTransaction.partnerBusinessName
                                ? `${OrderTransaction.siteName} (${OrderTransaction.partnerBusinessName})`
                                : OrderTransaction.siteName} />
                              <TransactionColumn>
                                <p className="mb-0">
                                  {new Date(OrderTransaction.loadedAt).toLocaleDateString()}
                                </p>
                              </TransactionColumn>
                              <td className="py-2 align-middle text-end">
                                <div className="dropdown">
                                  <Button className="dropdown-toggle btn-ghost-secondary" variant="link" id="actionsMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                                    <strong>Actions</strong>
                                  </Button>
                                  <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                                      <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => setOrderTransactionToView(OrderTransaction)}>View</BoldButton>
                                      <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => setOrderTransactionToViewTicket(OrderTransaction)}>Ticket Image</BoldButton>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        {paginatedOrderTransactions.items.length === 0 && emptyRow}
                      </tbody>
                    </Table>
                  </PaginatedTable>
                }} />}
          </>}
      </>
    )}
  </div>;
}

export { OrderTransactionsListPage };